
import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Checkbox, Button, Link, Modal, CircularProgress
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { trackScreenView, trackClick } from '../lib/gtag';
import { products } from '@/lib/products';
import { allowedOrigins, getEnvFromDomain } from "@/lib/envHelper.client";

const check = <CheckIcon style={{ color: 'green' }} />;
const cross = <CloseIcon style={{ color: '#ccc' }} />;

const features = [
  { label: 'The Butler Bullseye (The Key to Everything)', bullseye: true, brief: true, freedom: true },
  { label: 'Core Complaint (Heart of the Case)', bullseye: true, brief: true, freedom: true },
  { label: 'Law Enforcement Edition (Affidavit + Pro Materials)', bullseye: false, brief: true, freedom: true },
  { label: 'Defendants, Violations & Exhibits', bullseye: false, brief: true, freedom: true },
  { label: 'Grok3 Forensic Reports (AI-powered Analysis)', bullseye: '0', brief: '3', freedom: '3' },
  { label: 'Decoding the Deep State (By the Numbers)', bullseye: false, brief: true, freedom: true },
  { label: 'Freedom Pack Tactical Toolkit (Action Tools Made Simple)', bullseye: false, brief: false, freedom: true },
  { label: 'The Camelot Murders (Ebook + PDF Edition)', bullseye: false, brief: false, freedom: true },
  { label: 'The Kohberger Dossier (Motive, Accomplices & Cover-Ups) *', bullseye: false, brief: false, freedom: true, bold: true },
  { label: 'Target-Rich Investigations — 7 Bombshell Reports', bullseye: '0', brief: '7', freedom: '7' },
  { label: 'Image Files (Maps & Visual Evidence)', bullseye: '3', brief: '14', freedom: '14' },
  { label: 'File Count', bullseye: '4', brief: '51', freedom: '76' }
];

const renderCell = (value) => {
  if (value === true) return check;
  if (value === false) return cross;
  return <span style={{ fontWeight: 'bold', color: '#333' }}>{value}</span>;
};

export default function BBFDownloadNow() {
  const sectionRef = useRef();
  const hasTrackedRef = useRef(false);
  const [checkboxAgreed, setCheckboxAgreed] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState('bbf_brief_plus_freedom_pack');
  const [loading, setLoading] = useState(false); // ✅ State for modal

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasTrackedRef.current) {
          trackScreenView('BBFDownloadNow');
          hasTrackedRef.current = true;
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.3 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleBuyNow = async () => {
    localStorage.setItem("bbf_purchase_intent", "true"); // ✅ Prevent popup
    if (!checkboxAgreed) {
      alert("⚠️ Please check the box to confirm you’ve read and agreed to the terms.");
      return;
    }

    setLoading(true); // ✅ Show popup immediately

    const productCode = selectedProduct;
    const env = getEnvFromDomain(window.location.hostname);
    const apiBaseUrl = env === "prod"
      ? "https://bbf-create-checkout.onrender.com"
      : "https://bbf-create-checkout-dev.onrender.com";

    const purchaseGuid = crypto.randomUUID();

    // console.log("📦 Sending checkout payload:", { productCode, purchaseGuid, env, apiBaseUrl });

    try {
      const res = await fetch(`${apiBaseUrl}/create-checkout-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ productCode, purchaseGuid, env }),
      });

      const data = await res.json();

      if (!res.ok) {
        alert(`Error: ${data.error || "Unknown error"}`);
        setLoading(false);
        return;
      }

      if (data.url) {
        window.location.href = data.url; // ✅ Redirect to Stripe
      } else {
        alert("Error creating Stripe session.");
        setLoading(false);
      }
    } catch (err) {
      console.error("❌ Checkout unexpected error:", err);
      alert("Unable to launch checkout.");
      setLoading(false);
    }
  };

  return (
    <Box
      id="download"
      ref={sectionRef}
      sx={{
        minHeight: '100vh',
        px: 3,
        py: 10,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
      }}
    >
      <div id="downloads" style={{ position: 'relative', top: '-96px', height: 0 }} />
      <Box display="flex" alignItems="center" justifyContent="center" mb={4}>
        <Box
          component="img"
          src="/assets/images/icon-download-brief.png"
          alt="Download Icon"
          sx={{ height: '36px', width: '36px', mr: 1.5 }}
        />
        <Typography variant="h3" component="h1" sx={{ fontWeight: 'bold' }}>
          Download The Butler Brief
        </Typography>
      </Box>

      <TableContainer
        component={Paper}
        sx={{
          width: '100%',          // ✅ take full available width
          maxWidth: '100%',       // ✅ no artificial cap
          overflowX: 'auto'       // ✅ allow scroll fallback on very small screens
        }}
      >
        <Typography variant="h6" sx={{ padding: 2 }}>
          Every Option Includes the Bullseye—Select Your Access Level
        </Typography>
        <Table
          size="small"
          sx={{
            width: '100%',
            tableLayout: 'fixed',
            '& th': {
              fontSize: '0.85rem',
              whiteSpace: 'nowrap',
              textAlign: 'center',
            },
            '& td': {
              textAlign: 'center',
              verticalAlign: 'middle',
            },
            // ✅ Override first column to be left aligned
            '& td:first-of-type, & th:first-of-type': {
              textAlign: 'left',
            },
            '& svg': {
              fontSize: '1rem',
            },
          }}
        >

            <TableHead>
              <TableRow>
                {/* Feature column */}
                <TableCell sx={{ width: { xs: '46%', sm: '40%' }, textAlign: 'left' }}>
                  <strong>Feature</strong>
                </TableCell>

                {/* Bullseye */}
                <TableCell sx={{ width: { xs: '18%', sm: '20%' } }}>
                  <strong>
                    <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                      Bullseye
                    </Box>
                    <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                      The Butler Bullseye
                    </Box>
                  </strong>
                </TableCell>

                {/* Brief */}
                <TableCell sx={{ width: { xs: '18%', sm: '20%' } }}>
                  <strong>
                    <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                      Brief
                    </Box>
                    <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                      The Butler Brief
                    </Box>
                  </strong>
                </TableCell>

                {/* Freedom Pack */}
                <TableCell sx={{ width: { xs: '18%', sm: '20%' } }}>
                  <strong>
                    <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                      Pack
                    </Box>
                    <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                      Freedom Pack
                    </Box>
                  </strong>
                </TableCell>
              </TableRow>
            </TableHead>


          <TableBody>
            {features.map((f, i) => (
              <TableRow key={i}>
                <TableCell>{f.bold ? <strong>{f.label}</strong> : f.label}</TableCell>
                <TableCell align="center">{renderCell(f.bullseye)}</TableCell>
                <TableCell align="center">{renderCell(f.brief)}</TableCell>
                <TableCell align="center">{renderCell(f.freedom)}</TableCell>
              </TableRow>
            ))}
            <TableRow sx={{ borderTop: '2px solid #ddd' }}/>
            <TableRow sx={{ borderTop: '2px solid #ddd', backgroundColor: '#f9f9f9' }}>
              <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>Price</TableCell>
              <TableCell align="center" sx={{ fontSize: '1.35rem', fontWeight: 'bold', color: '#2E7D32' }}>$19</TableCell>
              <TableCell align="center" sx={{ fontSize: '1.35rem', fontWeight: 'bold', color: '#1565C0' }}>$49</TableCell>
              <TableCell align="center" sx={{ fontSize: '1.35rem', fontWeight: 'bold', color: '#C62828' }}>$99</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>Download</strong></TableCell>
              <TableCell align="center">
                <Checkbox
                  checked={selectedProduct === 'bbf_bullseye'}
                  onChange={() => setSelectedProduct('bbf_bullseye')}
                />
              </TableCell>
              <TableCell align="center">
                <Checkbox
                  checked={selectedProduct === 'bbf_brief'}
                  onChange={() => setSelectedProduct('bbf_brief')}
                />
              </TableCell>
              <TableCell align="center">
                <Checkbox
                  checked={selectedProduct === 'bbf_brief_plus_freedom_pack'}
                  onChange={() => setSelectedProduct('bbf_brief_plus_freedom_pack')}
                />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Typography
        variant="body2"
        sx={{
          display: 'block',
          textAlign: 'left',
          maxWidth: '900px',
          color: '#444',
          mt: 2,
          mb: 2,
          fontSize: '0.85rem'
        }}
      >
        * The Kohberger Dossier – Follow the blood trail from Moscow, ID to Moscow, PA, exposing the hidden roots of the Russia Collusion Hoax.
      </Typography>

      <Box sx={{ mt: 3, maxWidth: 900, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Checkbox
          checked={checkboxAgreed}
          onChange={(e) => setCheckboxAgreed(e.target.checked)}
          sx={{
            padding: 0,
            marginTop: '2px',
            alignSelf: 'flex-start'
          }}
        />
        <Typography
          variant="body2"
          sx={{
            lineHeight: 1.5,
            textAlign: 'left',
            wordBreak: 'break-word'
          }}
        >
          I have read and agree to the{' '}
          <Link href="/docs/soar_terms-of-use.pdf" target="_blank" rel="noopener noreferrer">
            Terms of Use
          </Link>,{' '}
          <Link href="/docs/soar_privacy-policy.pdf" target="_blank" rel="noopener noreferrer">
            Privacy Policy
          </Link>, and{' '}
          <Link href="/docs/soar_terms-of-use.pdf" target="_blank" rel="noopener noreferrer">
            Disclaimers
          </Link>.{' '}
          I understand The Butler Bullseye, The Butler Brief, The Kohberger Dossier, The Camelot Murders, and all related materials
          are digital downloads, are not legal advice, and that all purchases are final and non-refundable.
        </Typography>
      </Box>

      <Button
        variant="contained"
        sx={{ mt: 3, fontSize: '1.25rem', px: 5, py: 1.5 }}
        onClick={() => {
          trackClick("Download Single Button");
          handleBuyNow();
        }}
      >
        {loading ? "Processing..." : "DOWNLOAD NOW"}
      </Button>

      <Typography variant="body2" sx={{ mt: 4, fontSize: '1rem', maxWidth: 500, mx: 'auto', textAlign: 'center' }}>
        ✅ Instant Download. ✅ No Logins Required. ✅ Works on any device.
      </Typography>

      {/* ✅ Loading Modal */}
      <Modal open={loading}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100vh',
            bgcolor: 'rgba(0, 0, 0, 0.7)',
            color: '#fff',
            textAlign: 'center',
            p: 3
          }}
        >
          <CircularProgress size={60} sx={{ color: 'white', mb: 2 }} />
          <Typography variant="h6" sx={{ mb: 1 }}>
            Preparing your secure checkout...
          </Typography>
        </Box>
      </Modal>

      {import.meta.env.DEV && (
        <div
          style={{
            position: 'fixed',
            bottom: '5px',
            right: '10px',
            fontSize: '11px',
            color: 'limegreen',
            opacity: 0.6,
            zIndex: 9999,
            pointerEvents: 'none'
          }}
        >
          DEV
        </div>
      )}
    </Box>
  );
}
